import { CSSResultGroup, html, css, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './ghs-breadcrumbs.css.lit';
import '../ghs-icon/ghs-icon';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { SlotController } from '../../controllers/slot-controller';
import { classMap } from 'lit/directives/class-map.js';

/**
 * The breadcrumbs component.
 * @element ghs-breadcrumbs
 */

@customElement('ghs-breadcrumbs')
export class GhsBreadcrumbs extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles, css``];

  slots = new SlotController(this, true);

  /**
   * Variant for modifying breadcrumbs styles
   */
  @property({ type: String, attribute: 'style-variant' })
  styleVariant: String;

  @property({ type: Boolean, attribute: 'return-link', reflect: true })
  returnLink = false;

  /**
   * Create a ghs-icon element.
   *
   * @param name String
   * @param size Number
   * @returns HTMLElement
   */
  _createIconElement(name: string, size = 12): HTMLElement {
    const iconEl: HTMLElement = document.createElement('ghs-icon');
    iconEl.setAttribute('name', name);
    iconEl.setAttribute('size', `${size}px`);

    return iconEl;
  }

  /**
   * Create a separator element for the breadcrumb.
   *
   * @param className String
   * @param content String
   * @returns HTMLElement
   */
  _createSeparatorTextElement(className: string, content: string): HTMLElement {
    const el: HTMLElement = document.createElement('span');
    el.classList.add(className);
    el.innerHTML = content;

    return el;
  }

  addSeparatorIcon() {
    const breadcrumbItems: NodeListOf<HTMLSpanElement> = this.querySelectorAll(
      'span:not(span:last-child)'
    );

    breadcrumbItems.forEach(item => {
      let sep;
      this.site === 'BH'
        ? (sep = this._createSeparatorTextElement(
            'ghs-breadcrumbs__separator',
            '/'
          ))
        : (sep = this._createIconElement('chevron-right'));
      sep.classList.add('separator');

      item.append(sep);
    });
  }

  addBackSeparatorIcon() {
    const breadcrumbItems: NodeListOf<HTMLSpanElement> = this.querySelectorAll(
      'span:nth-last-of-type(2)'
    );

    breadcrumbItems.forEach(item => {
      const sep = this._createIconElement(
        this.site == 'BH' ? 'arrow-left' : 'chevron-left'
      );
      sep.classList.add('separator--back');

      item.prepend(sep);
    });
  }

  addReturnBackIcon() {
    const breadcrumbItem: HTMLSpanElement | null = this.querySelector('span');
    const backIcon = this._createIconElement(
      this.site == 'BH' ? 'arrow-left' : 'chevron-left'
    );

    if (breadcrumbItem) {
      breadcrumbItem.prepend(backIcon);
    }
  }

  firstUpdated(): void {
    super.firstUpdated();
    // firstUpdated can, in certain circumstances, run twice. This prevents that behavior.
    if (this.getAttribute('data-once')) {
      return;
    }
    this.setAttribute('data-once', 'true');
    if (!this.returnLink) {
      this.addSeparatorIcon();
      this.addBackSeparatorIcon();
    } else {
      this.addReturnBackIcon();
    }
  }

  render(): TemplateResult {
    const classes = {
      'breadcrumbs': true,
      'return-link': this.returnLink,
      [`${this.styleVariant}`]: this.styleVariant != '',
    };

    return html`
      <div class="${classMap(classes)}">
        <slot></slot>
      </div>
    `;
  }
}
